import clsx from 'clsx';
import { useEffect, useState } from 'react';
import useCarList from '../../hooks/react-query/useCarData';
import useOrders from '../../hooks/react-query/useOrder';
import Carousel from 'react-multi-carousel';
import { SliderCar } from '../../components/cars/sliderCar';
import { RightArrow, LeftArrow } from '../../icons/arrows/carouselArrows';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getDriversByPlateNumberFn } from '../../transactions/drivers/drivers';
import {
  useDeleteDrivers,
  useSetPrimaryDriver,
  useUnAssignDrivers,
} from '../../hooks/react-query/useDrivers';
import { useServiceCarStore } from '../../store/serviceCarStore';
import { OffersListing } from '../../components/cars/offersListing';
import { filterOrderedCarList } from '../../utils/helper';
import usePersonStore from '../../store/personStore';
import { QUERY_KEYS } from '../../config/constants';

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 100 },
    items: 1,
    // partialVisibilityGutter: 500
  },
};

const { CARS } = QUERY_KEYS;

export const RetailDashboard = () => {
  const assignedRoles = usePersonStore((store) => store.userRoles);
  const hasOnlyDriversRole =
    assignedRoles.length === 1 && assignedRoles.includes('driver');

  const [type, setType] = useState();
  const [carDrivers, setCarDrivers] = useState([]);
  const [activeCar, setActiveCar] = useState(false);
  const [orderCarList, setOrderCarList] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const { setCarInfo } = useServiceCarStore();

  const queryClient = useQueryClient();
  const { data: carList } = useCarList();
  const { data: ordersList, isLoading } = useOrders(!hasOnlyDriversRole);

  const { mutate: deleteDriver } = useDeleteDrivers();
  const { mutate: unAssignDriver } = useUnAssignDrivers();
  const { mutate: setPrimaryDriver } = useSetPrimaryDriver();

  const {
    pending: driversListPending,
    isError: driversListisError,
    data: driversList,
    error: driversListError,
  } = useQuery({
    queryKey: ['usersPerCar', activeCar?.plateno],
    queryFn: () => getDriversByPlateNumberFn(activeCar?.plateno),
    // enabled: !!activeCar.plateno,
    onSuccess: (data) => {
      setCarDrivers(data.data);
    },
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled: !!activeCar && !!activeCar.plateno && !hasOnlyDriversRole,
  });

  useEffect(() => {
    if (carList) {
      const orderList = filterOrderedCarList(ordersList, carList);
      setOrderCarList(orderList);
    }
  }, [carList, ordersList]);

  const cachedData = queryClient.getQueryData(['usersPerCar', activeCar?.plateno]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // Add event listener for resize
    window.addEventListener('resize', handleResize);

    // Clean up by removing the event listener
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (driversListPending) {
      setCarDrivers([]);
    } else if (cachedData) {
      setCarDrivers(cachedData.data);
    }
  }, [driversListPending, cachedData]);

  useEffect(() => {
    if (carList && type === 'leased') {
      setActiveCar(carList.data[0]);
      setCarInfo(carList.data[0]);
    }
  }, [type]);

  useEffect(() => {
    if (carList && carList.data?.length) {
      setType('leased');
      setActiveCar(carList.data[0]);
      setCarInfo(carList.data[0]);
    } else if (orderCarList && orderCarList.data && orderCarList.data.length) {
      setType('ordered');
    }
  }, [carList, orderCarList]);

  const handleClickAction = (actionType, id) => {
    switch (actionType) {
      case 'setPrimaryDriver':
        setPrimaryDriver(
          { userId: id, plateno: activeCar.plateno },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: ['usersPerCar'],
              });
            },
          }
        );
        break;
      case 'unAssignDriver':
        unAssignDriver(
          { id, plateno: activeCar.plateno },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: ['usersPerCar'],
              });
              queryClient.invalidateQueries({
                queryKey: [CARS],
              });
            },
          }
        );
        break;
      case 'deleteDriver':
        deleteDriver(id, {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['usersPerCar'] });
            queryClient.invalidateQueries({
              queryKey: [CARS],
            });
          },
        });
        break;
      default:
        break;
    }
  };

  const CustomButtonGroup = ({ next, previous, ...rest }) => {
    const { carouselState } = rest;
    return (
      <>
        {activeCar && (
          <div className="w-full items-center justify-center max-lg:gap-6 lg:gap-14 flex pt-8">
            {carList.data.length !== 1 &&
              <div
              className={clsx("h-10 w-10 rounded-full bg-white flex items-center justify-center", carouselState.currentSlide === 0 ? 'opacity-70' : 'cursor-pointer')}
              onClick={() => carouselState.currentSlide !== 0 && previous()}
            >
              <LeftArrow />
            </div>
            }
            <h2 className='text-center'>
              {activeCar.carTitle ?? activeCar.ModelName + ' ' +activeCar.MakeName}
            </h2>
            {carList.data.length !== 1 &&
              <div
                className={clsx("h-10 w-10 rounded-full bg-white flex items-center justify-center", carouselState.currentSlide === carouselState.totalItems - 1 ? 'opacity-70' : ' cursor-pointer')}
                onClick={() => carouselState.currentSlide !== carouselState.totalItems - 1 && next()}
              >
                <RightArrow />
              </div>
            }
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className={'overflow-auto scrollbar max-lg:flex max-lg:flex-col'}
      style={{
        height: 'calc(100vh - 140px)',
      }}
    >
      <div className="pt-8 container mx-auto flex items-center justify-center relative">
        <h1 className="max-xl:hidden absolute left-0">My Cars</h1>
        {!hasOnlyDriversRole && (
          <div
            className={clsx(
              'flex rounded-[8px] p-1 h-fit',
              type !== undefined && ' border  border-[#A5A5A5]'
            )}
          >
            {carList && carList.data?.length ? (
              <button
                className={clsx(
                  'text-secondary-button py-2 px-8 cursor-pointer rounded-[6px]',
                  type === 'leased'
                    ? 'text-white bg-black'
                    : 'text-[#6A6A6A] bg-transparent'
                )}
                onClick={() => {
                  setType('leased');
                }}
              >
                Μισθώσεις
              </button>
            ) : (
              <></>
            )}

            {orderCarList && orderCarList.data?.length ? (
              <button
                className={clsx(
                  'text-secondary-button py-2 px-8 cursor-pointer rounded-[6px]',
                  type === 'ordered'
                    ? 'text-white bg-black'
                    : 'text-[#6A6A6A] bg-transparent'
                )}
                onClick={() => {
                  setType('ordered');
                }}
              >
               Εκκρεμείς Παραγγελίες
              </button>
            ) : (
              <></>
            )}
          </div>
        )}
      </div>

      {type === 'leased' ? (
        <div className="w-full flex flex-col max-lg:flex-1">
          <Carousel
            responsive={responsive}
            infinite={false}
            transitionDuration={0}
            containerClass={clsx(
              'w-full order-last max-lg:h-full',
              carList.data?.length === 1 && 'justify-center',
              carList.data?.length &&
                carList.data?.length > 1 &&
                ((activeCar?.plateno !== undefined &&
                  activeCar?.plateno === carList.data[0].plateno) ||
                  (activeCar?.cncode !== undefined &&
                    activeCar?.cncode === carList.data[0].cncode)) &&
                'lg:pl-[25vw]'
            )}
            //this was commented out for center mode and pl was 25
            // itemClass='flex justify-center !w-fit'
            sliderClass="max-lg:h-full"
            arrows={false}
            centerMode={windowWidth > 990}
            renderButtonGroupOutside
            // partialVisbile={true}
            beforeChange={(nextSlide, props) => {
              setCarInfo(carList.data[nextSlide]);
              setActiveCar(carList.data[nextSlide]);
            }}
            swipeable={false}
            draggable={false}
            customButtonGroup={<CustomButtonGroup />}
          >
            {carList.data.map((item, index) => (
              <SliderCar
                handleClickAction={(type, id) => {
                  handleClickAction(type, id);
                }}
                driversList={carDrivers}
                carInfo={item}
                key={item.plateno || index}
                hasOnlyDriversRole={hasOnlyDriversRole}
                active={
                  (item.plateno && item.plateno === activeCar.plateno) ||
                  (item.cncode && item.cncode === activeCar.cncode)
                }
              />
            ))}
          </Carousel>
        </div>
      ) : (
        !isLoading && orderCarList && orderCarList.data &&
          <OffersListing ordersList={orderCarList.data} />
      )}
    </div>
  );
};
